import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, Check, CheckCheck, Image, FileText, RefreshCw, Loader2, ArrowLeft, User, Receipt, MessageCircle } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@vaiu/ui/avatar';
import { ScrollArea } from '@vaiu/ui/scroll-area';
import { CurrencyDisplay } from '@vaiu/ui';
import { useToast } from '@/hooks/use-toast';
import { ConversationMessage, platformApi } from '@/services/platformApi'
import { currencyService } from '@/services/currencyService';;
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@vaiu/ui';

interface WhatsAppStyleMessagingProps {
  // For transaction-based messaging
  transactionId?: string;
  transactionAmount?: number;
  transactionCurrency?: string;
  
  // For direct messaging
  contactId?: string;  // This is the user ID (contact.id)
  
  // Common props
  counterpartyName: string;
  counterpartyEmail?: string;
  className?: string;
  onBack?: () => void;
}

const WhatsAppStyleMessaging: React.FC<WhatsAppStyleMessagingProps> = ({
  transactionId,
  transactionAmount,
  transactionCurrency,
  contactId,
  counterpartyName,
  counterpartyEmail,
  className = '',
  onBack
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  const currentUserId = user?._id || user?.id || '';
  
  // Determine conversation mode
  const conversationMode = transactionId ? 'transaction' : 'direct';
  const conversationId = transactionId || contactId || '';

  useEffect(() => {
    if (conversationId) {
      console.log('💬 WhatsAppStyleMessaging: Loading conversation:', {
        mode: conversationMode,
        id: conversationId
      });
      fetchMessages();
    } else {
      console.warn('💬 WhatsAppStyleMessaging: No conversation ID provided');
    }
  }, [conversationId, transactionId, contactId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      let conversationMessages: ConversationMessage[] = [];
      
      if (conversationMode === 'transaction' && transactionId) {
        // Transaction-based conversation
        console.log('📥 Fetching transaction conversation via v2 API:', { transactionId });
        const v2Response = await platformApi.getConversationV2(transactionId);
        // v2 API returns { type, transactionId, conversation, posts? }
        // v2 format: conversation is array of { text, userId, timestamp, ... }
        // Map v2 format to ConversationMessage format
        conversationMessages = (v2Response.conversation || []).map((msg: any, index: number) => {
          // Check if it's already in ConversationMessage format
          if (msg.message && msg.senderId && msg.createdAt) {
            return msg as ConversationMessage;
          }
          
          // v2 format - map { text, userId, timestamp } to ConversationMessage
          return {
            id: msg.id || msg._id || `msg-${Date.now()}-${index}`,
            transactionId: v2Response.transactionId || transactionId,
            senderId: msg.userId || msg.senderId || '',
            senderName: msg.userName || msg.senderName || msg.name || 'User',
            message: msg.text || msg.message || '',
            createdAt: msg.timestamp || msg.createdAt || new Date().toISOString(),
            readAt: msg.readAt,
            attachments: msg.attachments
          } as ConversationMessage;
        });
      } else if (conversationMode === 'direct' && contactId) {
        // Direct conversation - API uses userId parameter
        console.log('📥 Fetching direct conversation via v2 API:', { contactId });
        const response = await platformApi.getDirectConversation(contactId);
        // Map API response format to ConversationMessage format
        conversationMessages = (response.conversation || []).map((msg: any, index: number) => {
          // API returns: { text, userId, timestamp }
          // Map to: { id, message, senderId, senderName, createdAt }
          return {
            id: msg.id || msg._id || `msg-${Date.now()}-${index}`,
            transactionId: undefined,  // No transaction for direct messages
            senderId: msg.userId || msg.senderId || '',
            senderName: msg.userName || msg.senderName || msg.name || 'User',
            message: msg.text || msg.message || '',
            createdAt: msg.timestamp || msg.createdAt || new Date().toISOString(),
            readAt: msg.readAt,
            attachments: msg.attachments
          } as ConversationMessage;
        });
      }
      
      setMessages(conversationMessages);
      console.log('✅ Loaded conversation via v2 API:', conversationMessages.length, 'messages');
    } catch (error: any) {
      const errorMessage = error?.message || String(error) || '';
      console.error('❌ Error fetching messages:', errorMessage);
      console.error('Full error:', error);
      
      // Check for 500 Internal Server Error - backend bug
      const isServerError = errorMessage.toLowerCase().includes('500') ||
                           errorMessage.toLowerCase().includes('internal server error') ||
                           errorMessage.toLowerCase().includes('this.app.get is not a function') ||
                           errorMessage.toLowerCase().includes('is not a function');
      
      // Check for 404 or not_found - this is normal if conversation doesn't exist yet
      const isNotFound = errorMessage.toLowerCase().includes('404') ||
                        errorMessage.toLowerCase().includes('not_found') ||
                        errorMessage.toLowerCase().includes('not found') ||
                        errorMessage.toLowerCase().includes('unhandled api request');
      
      // Check for HTML parsing error (backend returning HTML instead of JSON)
      const isHtmlError = errorMessage.includes('Unexpected token') || 
                         errorMessage.includes('DOCTYPE') ||
                         errorMessage.includes('Backend service is unavailable');
      
      // Check if backend endpoint is not implemented
      const isNotImplemented = errorMessage.toLowerCase().includes('unhandled api request');
      
      // Check for 403 Forbidden (direct message requires counterparty relationship)
      const isForbidden = errorMessage.includes('403') || errorMessage.includes('Forbidden');
      
      if (isServerError) {
        toast({
          title: 'Backend Error (500)',
          description: `The backend encountered an error: "${errorMessage}". This is a backend bug that needs to be fixed. Please contact the backend team.`,
          variant: 'destructive',
        });
      } else if (isForbidden && conversationMode === 'direct') {
        toast({
          title: 'Cannot send message',
          description: 'You can only message users you have transacted with.',
          variant: 'destructive',
        });
      } else if (!isNotFound && !isHtmlError && !isNotImplemented) {
        toast({
          title: 'Error loading messages',
          description: 'Failed to load conversation. Please try again.',
          variant: 'destructive',
        });
      }
      
      // Set empty messages array (conversation doesn't exist yet is normal, or endpoint not implemented)
      setMessages([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchMessages(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    // Add optimistic message immediately
    const optimisticMessage: ConversationMessage = {
      id: `temp-${Date.now()}`,
      transactionId: transactionId,
      senderId: currentUserId,
      senderName: 'You',
      message: messageText,
      createdAt: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setSending(true);

    try {
      if (conversationMode === 'transaction' && transactionId) {
        // Use v2 API for transaction messages
        console.log('📤 Sending transaction message via v2 API:', {
          transactionId,
          messageLength: messageText.length,
          timestamp: new Date().toISOString()
        });
        await platformApi.sendMessageV2(transactionId, messageText);
        console.log('✅ Transaction message sent via v2 API');
      } else if (conversationMode === 'direct' && contactId) {
        // Use v2 API for direct messages
        console.log('📤 Sending direct message via v2 API:', {
          contactId,
          messageLength: messageText.length,
          timestamp: new Date().toISOString()
        });
        await platformApi.sendDirectMessage(contactId, messageText);
        console.log('✅ Direct message sent via v2 API');
      }
      
      // Refresh messages to get the real message from server
      setTimeout(fetchMessages, 500);
    } catch (error: any) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      
      const errorMessage = error?.message || String(error) || '';
      console.error('❌ Final error sending message:', errorMessage);
      console.error('Full error object:', error);
      
      // Normalize error message for checking
      const normalizedError = errorMessage.toLowerCase().replace(/[_-]/g, ' ');
      
      // Check for 404/not found
      const isNotFound = normalizedError.includes('404') ||
                        normalizedError.includes('not found') ||
                        errorMessage === 'not_found' ||
                        errorMessage === 'not found';
      
      // Check for HTML parsing error (backend returning HTML instead of JSON)
      const isHtmlError = errorMessage.includes('Unexpected token') || 
                         errorMessage.includes('DOCTYPE') ||
                         errorMessage.includes('Backend service is unavailable');
      
      // Check if backend endpoint is not implemented
      const isNotImplemented = normalizedError.includes('unhandled api request');
      
      // Check for 403 Forbidden (direct message requires counterparty relationship)
      const isForbidden = normalizedError.includes('403') || normalizedError.includes('forbidden');
      
      if (isForbidden && conversationMode === 'direct') {
        toast({
          title: 'Cannot send message',
          description: 'You can only message users you have transacted with.',
          variant: 'destructive',
        });
      } else if (isNotImplemented) {
        toast({
          title: 'Messaging feature coming soon',
          description: 'The messaging API endpoints are not yet implemented on the backend. This feature will be available soon.',
          variant: 'default',
        });
      } else if (isNotFound) {
        toast({
          title: 'Backend Error: Conversation Not Found',
          description: `The backend returned "not_found" when trying to send a message. The conversation should be automatically created when sending the first message. Transaction ID: ${transactionId}. This is a backend issue - the conversation API should auto-create conversations.`,
          variant: 'destructive',
        });
      } else if (isHtmlError) {
        toast({
          title: 'Service unavailable',
          description: 'The messaging service is temporarily unavailable. Please try again later.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Failed to send message',
          description: errorMessage || 'Message could not be sent. Please try again.',
          variant: 'destructive',
        });
      }
      
      // Restore message text if sending failed
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select a file smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadingFile(true);
      
      // File attachments currently require transactionId
      if (conversationMode === 'transaction' && transactionId) {
        const attachment = await platformApi.uploadAttachment(file, transactionId);
        
        // Send a message with the attachment via v2 API
        await platformApi.sendMessageV2(transactionId, `📎 Sent ${attachment.filename}`);
      
        toast({
          title: 'File uploaded',
          description: `${attachment.filename} has been shared`,
        });
        
        // Refresh messages
        fetchMessages();
      } else {
        // Direct messages - file upload may not be supported yet
        toast({
          title: 'File upload not available',
          description: 'File attachments are currently only available for transaction conversations.',
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const renderAttachment = (attachment: any) => {
    const isImage = attachment.type?.startsWith('image/');
    
    return (
      <div key={attachment.id} className="mt-2 p-2 bg-black/5 dark:bg-white/5 rounded-lg">
        <div className="flex items-center space-x-2">
          {isImage ? (
            <Image className="w-4 h-4 text-muted-foreground" />
          ) : (
            <FileText className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium truncate">{attachment.filename}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => window.open(attachment.url, '_blank')}
            className="ml-auto h-6 w-6 p-0"
          >
            <FileText className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  const renderMessage = (message: ConversationMessage, index: number) => {
    const isOwnMessage = message.senderId === currentUserId;
    const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
    
    return (
      <div
        key={message.id}
        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-1 px-2`}
      >
        {!isOwnMessage && showAvatar && (
          <Avatar className="h-8 w-8 mr-2 flex-shrink-0">
            <AvatarImage src={`https://avatar.vercel.sh/${counterpartyEmail || counterpartyName}`} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium text-xs">
              {getInitials(counterpartyName)}
            </AvatarFallback>
          </Avatar>
        )}
        
        {!isOwnMessage && !showAvatar && (
          <div className="w-8 mr-2 flex-shrink-0" />
        )}
        
        <div className={`max-w-[75%] ${isOwnMessage ? 'flex flex-col items-end' : 'flex flex-col items-start'}`}>
          {showAvatar && !isOwnMessage && (
            <p className="text-xs text-muted-foreground mb-1 px-1">
              {message.senderName}
            </p>
          )}
          
          <div
            className={`inline-block px-3 py-2 rounded-lg ${
              isOwnMessage
                ? 'bg-[#005c4b] text-white rounded-tr-none'
                : 'bg-[#202c33] dark:bg-gray-700 text-white rounded-tl-none'
            }`}
            style={{
              // WhatsApp-style bubble with tail
              ...(isOwnMessage ? {
                borderTopRightRadius: '0.5rem',
                borderBottomRightRadius: '0.5rem',
                borderTopLeftRadius: '0.5rem',
                borderBottomLeftRadius: '0.5rem',
              } : {
                borderTopLeftRadius: '0.5rem',
                borderBottomLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                borderBottomRightRadius: '0.5rem',
              })
            }}
          >
            <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
            
            {message.attachments && message.attachments.map(renderAttachment)}
            
            <div className={`flex items-center justify-end gap-1 mt-1 ${
              isOwnMessage ? 'text-white/70' : 'text-white/70'
            }`}>
              <span className="text-xs">{formatMessageTime(message.createdAt)}</span>
              {isOwnMessage && (
                <div className="ml-1">
                  {message.readAt ? (
                    <CheckCheck className="w-3 h-3" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`flex justify-center py-8 ${className}`}>
        <LoadingSpinner text="Loading messages..." />
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-[#0b141a] dark:bg-gray-900 rounded-lg overflow-hidden h-full min-h-0 ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-700 p-3 bg-[#202c33] dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <Avatar className="h-8 w-8">
              <AvatarImage src={`https://avatar.vercel.sh/${counterpartyEmail || counterpartyName}`} />
              <AvatarFallback className="bg-primary/10 text-primary font-medium text-xs">
                {getInitials(counterpartyName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-medium text-white text-sm">{counterpartyName}</h3>
              <p className="text-xs text-gray-400">
                {conversationMode === 'transaction' && transactionAmount && transactionCurrency ? (
                  <span className="flex items-center gap-1">
                    <Receipt className="w-3 h-3" />
                    <CurrencyDisplay amount={transactionAmount} 
                      currency={transactionCurrency}
                      size="sm" currencyService={currencyService}
                    />
                  </span>
                ) : (
                  'Direct message'
                )}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Refresh messages"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 min-h-0 p-2 bg-[#0b141a] dark:bg-gray-900">
        {messages.length === 0 && !loading ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#202c33] dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium mb-2 text-white">Start the conversation</h3>
            {conversationMode === 'direct' && (
              <p className="text-gray-400 text-sm">
                Send a message to start chatting
              </p>
            )}
          </div>
        ) : messages.length > 0 ? (
          <div>
            {messages.map((message, index) => renderMessage(message, index))}
            <div ref={messagesEndRef} />
          </div>
        ) : null}
      </ScrollArea>

      {/* Message Input */}
      <div className="border-t border-gray-700 p-3 bg-[#202c33] dark:bg-gray-800 flex-shrink-0">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile || conversationMode === 'direct'}
            className="h-9 w-9 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
            title={conversationMode === 'direct' ? 'File upload not available for direct messages' : 'Attach file'}
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            disabled={sending || uploadingFile}
            className="flex-1 bg-[#2a3942] dark:bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 focus:border-gray-500"
          />
          
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending || uploadingFile}
            className="h-9 w-9 p-0 bg-[#005c4b] hover:bg-[#006d5b] text-white"
          >
            {sending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
        
        {uploadingFile && (
          <p className="text-sm text-gray-400 mt-2 flex items-center">
            <LoadingSpinner size="sm" className="mr-2" />
            Uploading file...
          </p>
        )}
      </div>
    </div>
  );
};

export default WhatsAppStyleMessaging;

